import { useEffect, useState } from "react";
import useRemSize from "../../hooks/useRemSize";
import { fetchSystemInfo as apiFetchSystemInfo } from "../../services/api/v3";

function Dashboard(props) {
  const remSize = useRemSize();
  
  // 状态管理
  const [systemData, setSystemData] = useState({
    cpu: { usage: 0 },
    memory: { usage: 0 },
    network: {
      upload: { current: 0, max: 20000000 }, // 20 Mbps = 20,000,000 bps
      download: { current: 0, max: 20000000 } // 20 Mbps = 20,000,000 bps
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 获取系统信息的函数
  const fetchSystemInfo = async () => {
    try {
      const data = await apiFetchSystemInfo();
      
      // 更新系统数据
      setSystemData({
        cpu: { usage: data.cpu_percent / 100 }, // 转换为0-1范围
        memory: { usage: data.memory_percent / 100 }, // 转换为0-1范围
        network: {
          upload: { 
            current: data.upload_rate_bps || 0, 
            max: 20000000 // 固定为20Mbps
          },
          download: { 
            current: data.download_rate_bps || 0, 
            max: 20000000 // 固定为20Mbps
          }
        }
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch system info:', err);
      setError(err.message);
      
      // 连接失败时将所有值设为0
      setSystemData({
        cpu: { usage: 0 },
        memory: { usage: 0 },
        network: {
          upload: { current: 0, max: 20000000 },
          download: { current: 0, max: 20000000 }
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // 组件挂载时获取数据并设置轮询
  useEffect(() => {
    // 立即获取一次数据
    fetchSystemInfo();
    
    // 设置轮询，每5秒获取一次数据
    const intervalId = setInterval(fetchSystemInfo, 5000);
    
    // 清理函数
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // 格式化带宽函数
  const formatBandwidth = (bytesPerSecond) => {
    if (bytesPerSecond < 1000) {
      return `${bytesPerSecond.toFixed(1)} bps`;
    } else if (bytesPerSecond < 1000000) {
      return `${(bytesPerSecond / 1000).toFixed(1)} Kbps`;
    } else if (bytesPerSecond < 1000000000) {
      return `${(bytesPerSecond / 1000000).toFixed(1)} Mbps`;
    } else {
      return `${(bytesPerSecond / 1000000000).toFixed(1)} Gbps`;
    }
  };

  // 计算百分比
  const calculatePercentage = (current, max) => {
    return Math.min(current / max, 1);
  };

  // 圆形进度条组件
  const CircularProgress = ({
    progress,
    title,
    value,
    size = 120,
    strokeWidth = 10,
    bgColor = '#e5e7eb',
    progressColor = '#10b981'
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const arcLength = circumference * 0.75;
    const progressLength = progress * arcLength;
    const rotationAngle = 137;
    
    return (
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={bgColor}
            strokeWidth={strokeWidth/2}
            strokeDasharray={`${arcLength} ${circumference - arcLength}`}
            fill="none"
            transform={`rotate(${rotationAngle}, ${size/2}, ${size/2})`}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={progressColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${progressLength} ${circumference - progressLength}`}
            strokeLinecap="round"
            transform={`rotate(${rotationAngle}, ${size / 2}, ${size / 2})`}
            style={{ transition: 'stroke-dasharray 0.3s ease' }}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '1.5rem',fontWeight:'500', color: '#374151', marginBottom: '4px' }}>
            {title}
          </div>
          <div style={{ fontSize: '1rem',fontWeight:'400', color: '#6b7280' }}>{value}</div>
        </div>
      </div>
    );
  };

  // 网络流量条组件
  const NetworkTrafficBar = ({
    type,
    current,
    max,
    color,
    thickness = 8, // 默认厚度为8px
    showLabel = true // 是否显示标签
  }) => {
    const percentage = calculatePercentage(current, max);
    const formattedCurrent = formatBandwidth(current);
    const formattedMax = formatBandwidth(max);
   
    return (
      <div className="dashboard-network-traffic-item">
        {showLabel && (
          <div className="dashboard-network-header">
            <span className="dashboard-network-label">
              {type === 'upload' ? '上行速率' : '下行速率'}
            </span>
            <span className="dashboard-network-value">
              {formattedCurrent} / {formattedMax}
            </span>
          </div>
        )}
        <div
          className="dashboard-network-bar-container"
          style={{
            height: `${thickness}px`,
            borderRadius: `${thickness / 2}px` // 根据厚度自动调整圆角
          }}
        >
          <div
            className="dashboard-network-bar"
            style={{
              width: `${percentage * 100}%`,
              backgroundColor: color,
              height: '100%',
              borderRadius: `${thickness / 2}px` // 与容器保持一致
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-content">
      <div className="dashboard-realtimemonitor-container">
        <div className="dashboard-realtimemonitor-header">
          假人服务端
        </div>
       
        <div className="dashboard-monitor-grid">
          <div className="dashboard-monitor-1">
            {/* CPU监控 */}
            <div className="dashboard-monitor-card">
              <div className="dashboard-monitor-content">
                <CircularProgress
                  progress={systemData.cpu.usage}
                  title="CPU"
                  value={`${Math.round(systemData.cpu.usage * 100)}%`}
                  size={remSize*10}
                  progressColor="#3b82f6"
                  strokeWidth={remSize*0.55}
                />
              </div>
            </div>
            {/* 内存监控 */}
            <div className="dashboard-monitor-card">
              <div className="dashboard-monitor-content">
                <CircularProgress
                  progress={systemData.memory.usage}
                  title="内存"
                  value={`${Math.round(systemData.memory.usage * 100)}%`}
                  size={remSize*10}
                  strokeWidth={remSize*0.55}
                  progressColor="#8b5cf6"
                />
              </div>
            </div>
          </div>
          {/* 网络流量监控 */}
          <div className="dashboard-monitor-card dashboard-network-card">
            <div className="dashboard-network-content">
              <NetworkTrafficBar
                type="upload"
                current={systemData.network.upload.current}
                max={systemData.network.upload.max}
                color="#3b82f6"
                thickness={remSize*0.6}
              />
              <NetworkTrafficBar
                type="download"
                current={systemData.network.download.current}
                max={systemData.network.download.max}
                color="#8b5cf6"
                thickness={remSize*0.6}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;